// src/components/AuthScreen.jsx
import React, { useState } from 'react'
import { signIn, signUp } from '../lib/supabase'

export default function AuthScreen({ onSuccess }) {
  const [mode, setMode]       = useState('signin') // 'signin' | 'signup'
  const [email, setEmail]     = useState('')
  const [password, setPass]   = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState('')
  const [success, setSuccess] = useState('')

  async function handleSubmit() {
    setError('')
    setSuccess('')

    // Basic validation
    if (!email.trim())    { setError('Email is required.');    return }
    if (!password.trim()) { setError('Password is required.'); return }
    if (mode === 'signup') {
      if (password.length < 6)      { setError('Password must be at least 6 characters.'); return }
      if (password !== confirm)     { setError('Passwords do not match.');                 return }
    }

    setLoading(true)

    if (mode === 'signin') {
      const { error: err } = await signIn(email, password)
      setLoading(false)
      if (err) { setError(err.message); return }
      onSuccess?.()
    } else {
      const { error: err } = await signUp(email, password)
      setLoading(false)
      if (err) { setError(err.message); return }
      setSuccess('Account created! Check your email to confirm your account, then sign in.')
      setMode('signin')
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') handleSubmit()
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>

        {/* Logo */}
        <div style={styles.logoRow}>
          <div style={styles.logoIcon}>
            <CrosshairSVG />
          </div>
          <div>
            <div style={styles.logoName}>DISDownrange</div>
            <div style={styles.logoBrand}>by Dynamic Innovative Solutions</div>
          </div>
        </div>

        {/* Mode Toggle */}
        <div style={styles.toggle}>
          <button
            style={{ ...styles.toggleBtn, ...(mode === 'signin' ? styles.toggleActive : {}) }}
            onClick={() => { setMode('signin'); setError(''); setSuccess('') }}
          >
            Sign In
          </button>
          <button
            style={{ ...styles.toggleBtn, ...(mode === 'signup' ? styles.toggleActive : {}) }}
            onClick={() => { setMode('signup'); setError(''); setSuccess('') }}
          >
            Create Account
          </button>
        </div>

        {/* Title */}
        <div style={styles.title}>
          {mode === 'signin' ? 'Welcome back' : 'Create your account'}
        </div>
        <div style={styles.subtitle}>
          {mode === 'signin'
            ? 'Sign in to access your rifle profiles and ammo data'
            : 'Free account — upgrade to Pro anytime'}
        </div>

        {/* Success Message */}
        {success && (
          <div style={styles.successMsg}>{success}</div>
        )}

        {/* Form */}
        <div style={styles.form}>
          <div style={styles.fieldGroup}>
            <label style={styles.fieldLabel}>Email</label>
            <input
              style={styles.input}
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              autoCapitalize="none"
              autoCorrect="off"
            />
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.fieldLabel}>Password</label>
            <input
              style={styles.input}
              type="password"
              placeholder={mode === 'signup' ? 'Min. 6 characters' : '••••••••'}
              value={password}
              onChange={e => setPass(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>

          {mode === 'signup' && (
            <div style={styles.fieldGroup}>
              <label style={styles.fieldLabel}>Confirm Password</label>
              <input
                style={styles.input}
                type="password"
                placeholder="Re-enter password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={styles.errorMsg}>{error}</div>
          )}

          {/* Submit */}
          <button
            style={{ ...styles.submitBtn, opacity: loading ? 0.7 : 1 }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading
              ? 'Please wait...'
              : mode === 'signin' ? 'Sign In →' : 'Create Free Account →'}
          </button>
        </div>

        {/* Free tier info */}
        {mode === 'signup' && (
          <div style={styles.freeInfo}>
            <div style={styles.freeRow}><span style={styles.checkmark}>✓</span> 2 rifle profiles</div>
            <div style={styles.freeRow}><span style={styles.checkmark}>✓</span> Basic ammo library</div>
            <div style={styles.freeRow}><span style={styles.checkmark}>✓</span> Trajectory calculator</div>
            <div style={styles.freeRow}><span style={styles.lockmark}>🔒</span> <span style={styles.lockedFeature}>Full terminal performance data — Pro</span></div>
            <div style={styles.freeRow}><span style={styles.lockmark}>🔒</span> <span style={styles.lockedFeature}>Real-time weather + shot log — Pro</span></div>
          </div>
        )}

        {/* Skip for now */}
        <button style={styles.skipBtn} onClick={() => onSuccess?.()}>
          Continue without account →
        </button>

        <div style={styles.legalText}>
          By creating an account you agree to our Terms of Service.
          DISDownrange is a product of Dynamic Innovative Solutions LLC.
        </div>

      </div>
    </div>
  )
}

function CrosshairSVG() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <circle cx="12" cy="12" r="7" />
      <line x1="12" y1="2" x2="12" y2="5" />
      <line x1="12" y1="19" x2="12" y2="22" />
      <line x1="2" y1="12" x2="5" y2="12" />
      <line x1="19" y1="12" x2="22" y2="12" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    </svg>
  )
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 1000,
    background: 'rgba(0,0,0,0.92)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px',
    overflowY: 'auto',
  },
  card: {
    background: '#111111', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 16, padding: '28px 24px',
    width: '100%', maxWidth: 420,
    display: 'flex', flexDirection: 'column', gap: 18,
  },

  logoRow:   { display: 'flex', alignItems: 'center', gap: 12 },
  logoIcon:  {
    width: 36, height: 36, background: '#c8a96e', borderRadius: 8,
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0a0a0a', flexShrink: 0,
  },
  logoName:  { fontSize: 16, fontWeight: 700, color: '#f0ede8', letterSpacing: '0.03em' },
  logoBrand: { fontSize: 9, color: '#c8a96e', letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'Courier New, monospace' },

  toggle: {
    display: 'flex', background: '#1a1a1a',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10, padding: 3, gap: 3,
  },
  toggleBtn: {
    flex: 1, padding: '8px', border: 'none', borderRadius: 8,
    background: 'transparent', color: '#666',
    fontSize: 13, cursor: 'pointer', transition: 'all 0.2s',
    fontFamily: 'Georgia, serif',
  },
  toggleActive: { background: '#c8a96e', color: '#0a0a0a', fontWeight: 700 },

  title:    { fontSize: 18, fontWeight: 600, color: '#f0ede8' },
  subtitle: { fontSize: 12, color: '#666', fontFamily: 'Courier New, monospace', lineHeight: 1.5, marginTop: -10 },

  form:       { display: 'flex', flexDirection: 'column', gap: 14 },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
  fieldLabel: { fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: 'Courier New, monospace' },
  input: {
    background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 8, padding: '11px 14px', color: '#f0ede8',
    fontFamily: 'Courier New, monospace', fontSize: 14, width: '100%',
    outline: 'none', boxSizing: 'border-box',
  },

  errorMsg:   { fontSize: 12, color: '#e05c5c', fontFamily: 'Courier New, monospace', padding: '8px 12px', background: 'rgba(224,92,92,0.08)', borderRadius: 6, lineHeight: 1.4 },
  successMsg: { fontSize: 12, color: '#4caf7d', fontFamily: 'Courier New, monospace', padding: '8px 12px', background: 'rgba(76,175,125,0.08)', borderRadius: 6, lineHeight: 1.4, border: '1px solid rgba(76,175,125,0.2)' },

  submitBtn: {
    background: '#c8a96e', color: '#0a0a0a', border: 'none',
    borderRadius: 10, padding: '13px', fontSize: 14, fontWeight: 700,
    cursor: 'pointer', letterSpacing: '0.03em', fontFamily: 'Georgia, serif',
    marginTop: 4,
  },

  freeInfo: {
    background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 10, padding: '14px 16px',
    display: 'flex', flexDirection: 'column', gap: 8,
  },
  freeRow:        { display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: '#aaa', fontFamily: 'Courier New, monospace' },
  checkmark:      { color: '#4caf7d', fontSize: 13, flexShrink: 0 },
  lockmark:       { fontSize: 11, flexShrink: 0 },
  lockedFeature:  { color: '#555' },

  skipBtn: {
    background: 'none', border: 'none', color: '#555',
    fontSize: 12, cursor: 'pointer', fontFamily: 'Courier New, monospace',
    textAlign: 'center', padding: '4px 0',
    letterSpacing: '0.04em',
  },

  legalText: {
    fontSize: 10, color: '#333', fontFamily: 'Courier New, monospace',
    textAlign: 'center', lineHeight: 1.5,
  },
}
